import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  ExternalLink,
  Hash,
  Loader2,
  RefreshCw,
} from "@/shared/ui/icons";

import { Button, Carte, EnTeteDePanneau, Etiquette, Section } from "@/shared/ui";
import { api as cyranoApi, ApiError } from "@/core/api/client";
import type { CreatorOnboardingState } from "@/panels/creators/useCreatorOnboardingState";

/** Step 4 — l'user invite le bot dans un groupe Telegram. Quand le bot
 *  reçoit `my_chat_member`, il save vault_chat_id et envoie un message dans
 *  le groupe avec un bouton inline « Confirmer comme Vault ». L'user clique
 *  sur ce bouton dans Telegram, ce qui set `vault_verified_at` côté DB.
 *  Cf `_provision_vault_after_bot_added` + `_handle_vault_confirm_callback`
 *  dans `app/infra/telegram/webhook/_core.py`.
 *
 *  Plus aucun topic Telegram créé : le routing Free/Paid/GFE/… se fait via
 *  `ContentItem.catalog_tags` qu'on choisit dans le cockpit lors de
 *  l'enrichissement de chaque media (Vault tab).
 *
 *  Le cockpit poll `/onboarding-state` à 3s pendant cette étape (cf
 *  `CreatorOnboardingWizard.tsx`). Dès que `vault_verified_at` est set en
 *  DB, le wizard passe `done=true` et l'user peut continuer.
 *
 *  Le bouton « Relancer la demande » re-poste le message dans le groupe au
 *  cas où l'user l'a manqué ou supprimé.
 */

type VerifyOk = {
  ok: true;
  state: "prompted" | "confirmed";
  vault_chat_id: number | null;
  vault_verified_at: string | null;
};

type VerifyError = {
  ok: false;
  code: string;
  message: string;
};

const ERROR_HINT_KEYS: Record<string, string> = {
  missing_bot_token: "errors.missing_bot_token",
  telegram_unreachable: "errors.telegram_unreachable",
  bot_not_in_group: "errors.bot_not_in_group",
  unknown: "errors.unknown",
};

export interface TelegramGroupStepHandle {
  save: () => Promise<boolean>;
  isValid: boolean;
  isBusy: boolean;
}

interface TelegramGroupStepProps {
  cyranoCreatorId: string;
  telegramGroupState: CreatorOnboardingState["telegram_group"];
  botUsername: string | null;
  onValidityChange: (handle: TelegramGroupStepHandle) => void;
  externalError: string | null;
}

export function TelegramGroupStep({
  cyranoCreatorId,
  telegramGroupState,
  botUsername,
  onValidityChange,
  externalError,
}: TelegramGroupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<VerifyOk | VerifyError | null>(null);
  // Guard synchrone contre les double-clicks rapides (setBusy est async).
  const inFlightRef = useRef(false);

  const {
    done: vaultDone,
    vault_chat_id: vaultChatId,
    vault_chat_title: vaultChatTitle,
    notification_chat_id: notifChatId,
    notification_chat_title: notifChatTitle,
    notification_enabled: notifEnabled,
  } = telegramGroupState;

  // `admin=manage_topics` declenche le dialog "Add as admin" de Telegram quand
  // l'user clique le lien. Avec un SEUL right valide ca marche. En passer
  // plusieurs (change_info+delete_messages+...) — meme tous valides en Bot API
  // — fait que Telegram ignore silencieusement le param et l'ajoute comme
  // membre simple (regression en prod le 2026-05-17). Le bot recoit alors les
  // droits admin standard d'un membre admin promu via dialog Telegram, donc
  // peut voir les messages (le but).
  const inviteUrl = botUsername
    ? `https://t.me/${botUsername}?startgroup=true&admin=manage_topics`
    : null;

  const isValid = vaultDone || result?.ok === true;

  const runReprompt = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setBusy(true);
    setResult(null);
    try {
      const r = await cyranoApi.post<VerifyOk>(
        `/api/creators/${cyranoCreatorId}/verify-telegram-group`,
        {},
        { timeout: 30_000 },
      );
      setResult(r);
      await queryClient.invalidateQueries({
        queryKey: ["creator-onboarding-state"],
      });
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as {
          detail?: { code?: string; message?: string };
        } | null;
        const detail = body?.detail ?? null;
        const code = detail?.code ?? "unknown";
        const message = detail?.message ?? err.message;
        setResult({ ok: false, code, message });
      } else {
        setResult({
          ok: false,
          code: "unknown",
          message: err instanceof Error ? err.message : String(err),
        });
      }
      return false;
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  }, [cyranoCreatorId, queryClient]);

  useEffect(() => {
    onValidityChange({
      isValid,
      isBusy: busy,
      save: async () => {
        // L'user ne peut passer la step qu'apres avoir clique sur le bouton
        // inline cote Telegram (vault_verified_at set cote DB). Pas de
        // "save" forcable cote cockpit.
        return vaultDone;
      },
    });
  }, [vaultDone, busy, isValid, onValidityChange]);

  const vaultLabel = vaultChatTitle ?? (vaultChatId !== null ? `#${vaultChatId}` : null);
  const notifLabel = notifChatTitle ?? (notifChatId !== null ? `#${notifChatId}` : null);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-8">
      <header className="flex flex-col gap-1">
        <Etiquette>{t("shell.creatorOnboardingWizard.steps.telegram_group.short")}</Etiquette>
        <EnTeteDePanneau
          niveau="h3"
          titre={t("shell.creatorOnboardingWizard.telegramGroup.heading")}
          sousTitre={t("shell.creatorOnboardingWizard.telegramGroup.help")}
          className="border-b-0 p-0 [&_.sf-bandeau-titre]:whitespace-normal"
        />
      </header>

      {/* Vault group — 3 etats : confirmé / en attente clic / pas encore detecté */}
      {vaultDone ? (
        <Carte remplissage="md">
          <p className="flex items-center gap-2 text-[13px] font-semibold text-fg">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-valide" aria-hidden />
            {t("shell.creatorOnboardingWizard.telegramGroup.vaultDetected")}
          </p>
          {vaultLabel && (
            <p className="mt-1.5 text-[13px] text-muted">
              <span className="mr-1 text-faint">#</span>
              <span className="font-medium text-fg">{vaultLabel}</span>
            </p>
          )}
        </Carte>
      ) : vaultChatId !== null ? (
        // Bot detecté dans un groupe mais user n'a pas encore cliqué sur le
        // bouton inline cote Telegram.
        <Carte remplissage="md">
          <p className="flex items-center gap-2 text-[13px] font-semibold text-fg">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted" aria-hidden />
            {t("shell.creatorOnboardingWizard.telegramGroup.awaitingConfirmation.title")}
          </p>
          {vaultLabel && (
            <p className="mt-1.5 text-[13px] text-muted">
              <span className="mr-1 text-faint">#</span>
              <span className="font-medium text-fg">{vaultLabel}</span>
            </p>
          )}
          <p className="mt-2 text-[13px] leading-relaxed text-muted">
            {t("shell.creatorOnboardingWizard.telegramGroup.awaitingConfirmation.body")}
          </p>
        </Carte>
      ) : (
        // Bot pas encore dans un groupe → instructions
        <>
          <Section titre={t("shell.creatorOnboardingWizard.telegramGroup.howTo.title")}>
            <ol className="ml-4 list-decimal space-y-1.5 text-[13px] leading-relaxed text-muted">
              <li>{t("shell.creatorOnboardingWizard.telegramGroup.howTo.step1")}</li>
              <li>{t("shell.creatorOnboardingWizard.telegramGroup.howTo.step2")}</li>
              <li>{t("shell.creatorOnboardingWizard.telegramGroup.howTo.step3")}</li>
            </ol>
          </Section>

          <Section
            titre={t("shell.creatorOnboardingWizard.telegramGroup.invite.title")}
            sousTitre={t("shell.creatorOnboardingWizard.telegramGroup.invite.body")}
          >
            {inviteUrl ? (
              <Button
                variant="accent"
                size="lg"
                href={inviteUrl}
                target="_blank"
                icon={<ExternalLink className="h-3.5 w-3.5" />}
              >
                {t("shell.creatorOnboardingWizard.telegramGroup.invite.action", {
                  username: botUsername ?? "",
                })}
              </Button>
            ) : (
              <p className="text-[12px] text-faint">
                {t("shell.creatorOnboardingWizard.telegramGroup.invite.noBot")}
              </p>
            )}
          </Section>
        </>
      )}

      {/* Notification group — optionnel, juste pour info */}
      {notifChatId !== null && notifEnabled && (
        <Carte remplissage="md">
          <div className="flex items-start gap-3">
            <Bell className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
            <div className="text-[13px] text-fg">
              <p className="font-semibold">
                {t("shell.creatorOnboardingWizard.telegramGroup.notificationDetected")}
              </p>
              {notifLabel && (
                <p className="mt-1 text-muted">
                  <Hash className="mr-1 inline h-3 w-3" />
                  {notifLabel}
                </p>
              )}
            </div>
          </div>
        </Carte>
      )}

      {/* Bouton fallback — re-prompte le bouton inline dans le groupe.
          Utile si l'user a manque ou supprime le message du bot. */}
      {!vaultDone && vaultChatId !== null && (
        <Button
          variant="secondary"
          size="lg"
          onClick={() => {
            void runReprompt();
          }}
          disabled={busy}
          icon={
            busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )
          }
          className="self-start"
        >
          {t("shell.creatorOnboardingWizard.telegramGroup.repromptAction")}
        </Button>
      )}

      {(result && !result.ok) || externalError ? (
        <Carte remplissage="sm" role="alert">
          <div className="flex items-start gap-2.5 text-[13px] text-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-erreur" aria-hidden />
            {result && !result.ok ? (
              <div className="min-w-0">
                <p className="font-medium">
                  {t(
                    `shell.creatorOnboardingWizard.telegramGroup.${
                      ERROR_HINT_KEYS[result.code] ?? "errors.unknown"
                    }`,
                    { defaultValue: result.message },
                  )}
                </p>
                <p className="mt-0.5 text-muted">{result.message}</p>
              </div>
            ) : (
              <p className="min-w-0">{externalError}</p>
            )}
          </div>
        </Carte>
      ) : null}
    </div>
  );
}
